import { useEffect, useState } from "react";
import { Camera, FileDown, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, domaineLabel, errMsg, fmtDate, mediaUrl, STATUTS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PurgeTickets from "@/components/PurgeTickets";

const ReponseClient = ({ ticket, onSent }) => {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const envoyer = async () => {
    if (message.trim().length < 3) return toast.error("Message trop court");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/message`, { message });
      toast[data.ok ? "success" : "error"](data.ok ? "Réponse envoyée au client" : "Email non envoyé, réponse tout de même tracée");
      setMessage("");
      onSent();
    } catch (e) {
      toast.error(errMsg(e, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#005CFF]/30 bg-blue-50/40 p-4 space-y-3" data-testid="reponse-client-panel">
      <div className="bk-label flex items-center gap-2 text-[#0047CC]">
        <Send size={13} /> Répondre au client
      </div>
      <p className="text-xs text-slate-600">La réponse part par email et reste tracée dans le ticket (visible par le client dans son suivi).</p>
      {ticket.messages?.length > 0 && (
        <div className="space-y-2">
          {ticket.messages.map((m) => (
            <div key={`${m.date}-${m.auteur}`} data-testid={`ticket-message-${m.date}`} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.message}</p>
              <p className="text-[11px] text-slate-500 mt-1">
                {m.auteur} · {fmtDate(m.date)} · {m.envoye ? "envoyé" : "email non parti"}
              </p>
            </div>
          ))}
        </div>
      )}
      <Textarea data-testid="reponse-client-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Bonjour, un technicien passe demain matin entre 8h et 10h…" className="bg-white" />
      <Button data-testid="reponse-client-btn" onClick={envoyer} disabled={busy} size="sm" className="bg-[#005CFF] hover:bg-[#0047CC]">
        {busy ? "Envoi…" : "Envoyer au client"}
      </Button>
    </div>
  );
};

const PhotosResolution = ({ ticket, onChange }) => {
  const [files, setFiles] = useState([]);
  const [commentaire, setCommentaire] = useState("");
  const [busy, setBusy] = useState(false);

  const envoyer = async () => {
    if (!files.length) return toast.error("Choisissez au moins une photo");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("commentaire", commentaire);
      await api.post(`/admin/tickets/${ticket.id}/photos-resolution`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photos après intervention ajoutées");
      setFiles([]);
      setCommentaire("");
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (url) => {
    try {
      await api.delete(`/admin/tickets/${ticket.id}/photos-resolution`, { params: { url } });
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="photos-resolution-panel">
      <div className="bk-label flex items-center gap-2 text-emerald-800">
        <Camera size={13} /> Photos après intervention
      </div>
      <p className="text-xs text-slate-600">Visibles par le client sur son ticket et dans l'historique de l'équipement.</p>
      {ticket.photos_resolution?.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {ticket.photos_resolution.map((p) => (
            <div key={p.url} className="relative" data-testid={`resolution-photo-${p.url.split("/").pop()}`}>
              <img src={mediaUrl(p.url)} alt="" className="h-20 w-full object-cover rounded-lg border border-emerald-200" />
              <button
                data-testid={`resolution-photo-remove-${p.url.split("/").pop()}`}
                onClick={() => supprimer(p.url)}
                className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 p-1 text-slate-400 hover:text-red-500 shadow-sm"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        data-testid="resolution-photos-input"
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => setFiles(Array.from(e.target.files || []))}
        className="block w-full text-sm text-slate-600"
      />
      <Input
        data-testid="resolution-commentaire-input"
        value={commentaire}
        onChange={(e) => setCommentaire(e.target.value)}
        placeholder="Commentaire du technicien (ex : disjoncteur remplacé)"
        className="h-10 bg-white"
      />
      <Button data-testid="resolution-upload-btn" onClick={envoyer} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
        {busy ? "Envoi…" : "Ajouter les photos"}
      </Button>
    </div>
  );
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [filtre, setFiltre] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/tickets", { params: filtre ? { statut: filtre } : {} }).then(({ data }) => setTickets(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre]);

  const setStatut = async (t, statut) => {
    await api.patch(`/admin/tickets/${t.id}/statut`, { statut, commentaire: "Mise à jour depuis le back-office" });
    toast.success(`${t.reference} → ${statut}`);
    load();
    if (detail?.id === t.id) {
      const { data } = await api.get(`/tickets/${t.id}`);
      setDetail(data);
    }
  };

  const relancerWebhook = async (t) => {
    try {
      const { data } = await api.post(`/admin/tickets/${t.id}/webhook/retry`);
      toast[data.ok ? "success" : "error"](data.ok ? `${t.reference} remonté dans BKFlow` : `BKFlow injoignable : ${data.status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Relance impossible"));
    }
  };

  const supprimer = async (t) => {
    if (!window.confirm(`Supprimer définitivement le ticket ${t.reference} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/tickets/${t.id}`);
      toast.success(`Ticket ${t.reference} supprimé`);
      if (detail?.id === t.id) setDetail(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <div>
        <div className="bk-label mb-2">Alertes SAV</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Tickets</h1>
      </div>

      <PurgeTickets tickets={tickets} onPurge={load} />

      <div className="flex gap-2 flex-wrap">
        <button
          data-testid="admin-filter-all"
          onClick={() => setFiltre("")}
          className={`text-sm px-3 py-1.5 rounded-full border ${!filtre ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}
        >
          Tous
        </button>
        {STATUTS.map((s) => (
          <button
            key={s.key}
            data-testid={`admin-filter-${s.key}`}
            onClick={() => setFiltre(s.key)}
            className={`text-sm px-3 py-1.5 rounded-full border ${filtre === s.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Référence</th>
              <th className="px-4 py-3">Client / site</th>
              <th className="px-4 py-3">Domaine</th>
              <th className="px-4 py-3">Urgence</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Créé</th>
              <th className="px-4 py-3">Webhook</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`admin-ticket-row-${t.reference}`}>
                <td className="px-4 py-3 font-mono-bk text-xs">{t.reference}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{t.client?.nom}</div>
                  <div className="text-xs text-slate-500">{t.site_nom}</div>
                </td>
                <td className="px-4 py-3">{domaineLabel(t.domaine)}</td>
                <td className="px-4 py-3">{t.urgence}</td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`admin-statut-select-${t.reference}`}
                    value={t.statut}
                    onChange={(e) => setStatut(t, e.target.value)}
                    className="border border-slate-300 rounded-lg px-2 py-1 text-xs"
                  >
                    {STATUTS.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {t.webhook_status === "en_attente" ? (
                    "envoi en cours…"
                  ) : t.webhook_status?.startsWith("envoye_2") ? (
                    <span className="text-emerald-600">{t.webhook_status}</span>
                  ) : (
                    <button
                      data-testid={`webhook-retry-${t.reference}`}
                      onClick={() => relancerWebhook(t)}
                      title={t.webhook_status}
                      className="text-amber-600 hover:underline text-left"
                    >
                      {(t.webhook_status || "non envoyé").slice(0, 28)} · relancer
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={`admin-ticket-detail-${t.reference}`}
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const { data } = await api.get(`/tickets/${t.id}`);
                        setDetail(data);
                      }}
                    >
                      Voir
                    </Button>
                    <button
                      data-testid={`admin-ticket-delete-${t.reference}`}
                      onClick={() => supprimer(t)}
                      title="Supprimer le ticket"
                      className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">Aucun ticket.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{detail?.reference}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <Button
                data-testid="admin-rapport-pdf-btn"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/tickets/${detail.id}/rapport.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `rapport-${detail.reference}.pdf`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 20000);
                  } catch (e) {
                    toast.error(errMsg(e, "Rapport indisponible"));
                  }
                }}
              >
                <FileDown size={15} className="mr-2" /> Rapport d'intervention PDF
              </Button>
              {detail.signature_client ? (
                <div data-testid="admin-signature-client">
                  <div className="bk-label mb-1">Signature du client</div>
                  <img src={detail.signature_client} alt="signature" className="h-16 bg-white rounded-lg border border-slate-200" />
                </div>
              ) : detail.cloture_validee ? (
                <p className="text-xs text-slate-500" data-testid="admin-sans-signature">Clôture validée par le client, sans signature.</p>
              ) : null}
              <div>
                <div className="bk-label mb-1">Client</div>
                {detail.client?.nom} · {detail.client?.email} · {detail.client?.telephone}
              </div>
              <div>
                <div className="bk-label mb-1">Contrat</div>
                {detail.contrat?.libelle} {detail.contrat?.actif ? "(inclus)" : "(hors contrat)"}
              </div>
              <div>
                <div className="bk-label mb-1">Parcours QCM</div>
                {detail.qcm_path?.length
                  ? detail.qcm_path.map((s, i) => (
                      <div key={`${s.question}-${s.reponse}-${i}`}>
                        {s.question} → <strong>{s.reponse}</strong>
                      </div>
                    ))
                  : "—"}
              </div>
              <div>
                <div className="bk-label mb-1">Description</div>
                <p className="whitespace-pre-wrap">{detail.description_texte || "—"}</p>
              </div>
              <div>
                <div className="bk-label mb-1">Transcription vocale</div>
                <p className="whitespace-pre-wrap">{detail.transcription_vocale || "—"}</p>
              </div>
              {detail.photos?.length > 0 && (
                <div>
                  <div className="bk-label mb-1">Photos déclarées par le client</div>
                  <div className="grid grid-cols-3 gap-2">
                    {detail.photos.map((p, i) => (
                      <img key={`${p.slice(-32)}-${i}`} src={p} alt="" className="rounded-lg border border-slate-200 aspect-square object-cover" />
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="bk-label mb-1">Site</div>
                  {detail.site_nom || "—"}
                  <div className="text-xs text-slate-500">{detail.site_adresse || ""}</div>
                </div>
                <div>
                  <div className="bk-label mb-1">Matériel</div>
                  {detail.materiel_concerne || "—"}
                  <div className="text-xs text-slate-500">
                    {detail.materiel_marque ? `Marque ${detail.materiel_marque}` : ""}
                    {detail.materiel_marque && detail.materiel_serie ? " · " : ""}
                    {detail.materiel_serie ? `SN ${detail.materiel_serie}` : ""}
                  </div>
                </div>
              </div>
              {detail.video_url && (
                <div data-testid="admin-ticket-video">
                  <div className="bk-label mb-1">Vidéo du client</div>
                  <video src={mediaUrl(detail.video_url)} controls playsInline className="w-full rounded-lg border border-slate-200" />
                </div>
              )}
              <ReponseClient                ticket={detail}
                onSent={async () => {
                  const { data } = await api.get(`/tickets/${detail.id}`);
                  setDetail(data);
                }}
              />
              <PhotosResolution
                ticket={detail}
                onChange={async () => {
                  const { data } = await api.get(`/tickets/${detail.id}`);
                  setDetail(data);
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
